import type { UpdateExperienceRequest } from 'app/typings/core/admin/experiences/experienceForm';

import { MenuItemName } from './menuItem';

type FormOnlyField =
  | 'experienceType'
  | 'hotelId'
  | 'originalId'
  | 'lastMinuteRule'
  | 'maxAddonQuantity'
  | 'showInInventory'
  | 'roomServiceAvailable'
  | 'roomServiceIncluded';

type RenamedPayloadField = 'isRoomServiceAvailable' | 'isRoomServiceIncluded';

export type ExperienceFormField =
  | Exclude<keyof UpdateExperienceRequest, RenamedPayloadField>
  | FormOnlyField;

export const EXPERIENCE_TAB_FIELDS = {
  [MenuItemName.General]: [
    'categoryId',
    'experienceType',
    'hotelId',
    'originalId',
    'duration',
    'durationDescription',
    'location',
    'brand',
    'time',
    'emoji',
  ],
  [MenuItemName.Accessibility]: [
    'accessMode',
    'spaceIds',
    'roomServiceAvailable',
    'roomServiceIncluded',
    'roomServicePrice',
    'openingHours',
    'availableDayLimits',
    'closingPeriods',
    'lastMinuteRule',
    'closedDaysBeforeCheckIn',
    'maxAddonQuantity',
    'maxQuantity',
  ],
  [MenuItemName.Availability]: [
    'availabilityMode',
    'showInInventory',
    'allowedPaxTypes',
    'minPax',
    'maxPax',
    'isAlwaysIncludedWithNight',
    'openingConfiguration',
  ],
  [MenuItemName.Copywriting]: [
    'description',
    'tagline',
    'importantInformation',
    'menu',
    'treatmentList',
  ],
  [MenuItemName.Medias]: ['coverPicture', 'experiencePictures', 'emoji'],
} as const satisfies Record<MenuItemName, readonly ExperienceFormField[]>;

type MappedField = (typeof EXPERIENCE_TAB_FIELDS)[MenuItemName][number];
type UnmappedField = Exclude<ExperienceFormField, MappedField>;

export const everyExperienceFieldIsMapped: [UnmappedField] extends [never]
  ? true
  : UnmappedField = true;

type FieldLike = {
  name?: (string | number) | (string | number)[];
  errors?: string[];
};

const topLevelName = (name: FieldLike['name']): string =>
  String(Array.isArray(name) ? name[0] : name);

const ALL_MAPPED_FIELDS: ReadonlySet<string> = new Set(
  Object.values(EXPERIENCE_TAB_FIELDS).flat()
);

export const activeTabHasFieldError = (
  fields: FieldLike[],
  activeTab: MenuItemName
): boolean => {
  const tabFields = EXPERIENCE_TAB_FIELDS[activeTab] as readonly string[];

  return fields.some((field) => {
    if ((field.errors?.length ?? 0) === 0) {
      return false;
    }

    const name = topLevelName(field.name);

    return tabFields.includes(name) || !ALL_MAPPED_FIELDS.has(name);
  });
};

export const getRejectedFields = (error: unknown): FieldLike[] =>
  (error as { errorFields?: FieldLike[] })?.errorFields ?? [];
